import React from 'react'
import { Box, Typography } from '@mui/material'
import {
  VideoCameraIcon,
  MicrophoneIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline'
import { DefaultModal } from '../../../../shared/components/DefaultModal'
import { useMediaPermissions } from '../../../../shared/hooks/useMediaPermissions'
import { detectBrowser } from '../../helpers/browserDetection'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'

interface WherebyPermissionsModalProps {
  isOpen: boolean
  onClose: () => void
}

const P = 'whereby.permissions.modal'

const renderStep = (num: number, text: string, icon?: React.ReactNode) => (
  <Box
    key={num}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.5,
      borderRadius: '8px',
      backgroundColor: 'rgba(255,255,255,0.04)',
      mb: 1
    }}
  >
    <Box
      sx={{
        minWidth: 26,
        height: 26,
        borderRadius: '50%',
        backgroundColor: INTERAX_COLORS.heritageBlue,
        color: INTERAX_COLORS.white,
        fontWeight: 600,
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {num}
    </Box>
    {icon && <Box sx={{ color: INTERAX_COLORS.white, mt: '3px', flexShrink: 0 }}>{icon}</Box>}
    <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white, lineHeight: 1.6 }}>{text}</Typography>
  </Box>
)

export const WherebyPermissionsModal: React.FC<WherebyPermissionsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { camera, microphone } = useMediaPermissions()
  const browser = detectBrowser()

  const statusColor = (state: string) => {
    if (state === 'granted') return INTERAX_COLORS.green
    if (state === 'denied') return INTERAX_COLORS.redError
    return INTERAX_COLORS.sun
  }
  const statusLabel = (state: string) => {
    if (state === 'granted') return t(`${P}.enabled`, 'Enabled')
    if (state === 'denied') return t(`${P}.denied`, 'Blocked')
    return t(`${P}.prompt`, 'Not yet set')
  }

  const renderBrowserSteps = () => {
    const b = browser === 'unknown' ? null : browser
    if (!b) {
      return (
        <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white, lineHeight: 1.8 }}>
          {t(`${P}.unknown.description`)}
        </Typography>
      )
    }
    return (
      <>
        {renderStep(1, t(`${P}.${b}.step1`), <InformationCircleIcon style={{ width: 18, height: 18 }} />)}
        {renderStep(2, t(`${P}.${b}.step2`), <Cog6ToothIcon style={{ width: 18, height: 18 }} />)}
        {renderStep(
          3,
          t(`${P}.${b}.step3`),
          <Box sx={{ display: 'flex', gap: '3px' }}>
            <VideoCameraIcon style={{ width: 18, height: 18 }} />
            <MicrophoneIcon style={{ width: 18, height: 18 }} />
          </Box>
        )}
        {renderStep(
          4,
          t(`${P}.${b}.step4`),
          browser === 'safari' ? (
            <LockClosedIcon style={{ width: 18, height: 18 }} />
          ) : browser === 'firefox' ? (
            <ShieldCheckIcon style={{ width: 18, height: 18 }} />
          ) : (
            <ArrowPathIcon style={{ width: 18, height: 18 }} />
          )
        )}
      </>
    )
  }

  return (
    <DefaultModal isOpen={isOpen} onClose={onClose} maxWidth="520px">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: INTERAX_COLORS.white, mb: 0.5 }}>
            {t(`${P}.title`, 'Enable Camera & Microphone')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey }}>{t(`${P}.subtitle`)}</Typography>
        </Box>

        {/* Current Status */}
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: `1px solid rgba(255,255,255,0.08)`
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: INTERAX_COLORS.white, mb: 1.5 }}>
            {t(`${P}.currentStatus`, 'Current status')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              {
                icon: <VideoCameraIcon style={{ width: 16, height: 16 }} />,
                label: t(`${P}.camera`, 'Camera'),
                state: camera
              },
              {
                icon: <MicrophoneIcon style={{ width: 16, height: 16 }} />,
                label: t(`${P}.microphone`, 'Microphone'),
                state: microphone
              }
            ].map(({ icon, label, state }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: INTERAX_COLORS.white }}>{icon}</Box>
                <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white }}>
                  {label}:{' '}
                  <Box component="span" sx={{ fontWeight: 600, color: statusColor(state) }}>
                    {statusLabel(state)}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Browser Instructions */}
        <Box>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: INTERAX_COLORS.white, mb: 1.5 }}>
            {t(`${P}.howToEnable`, 'How to enable')}
            {browser !== 'unknown' && (
              <Box
                component="span"
                sx={{ color: INTERAX_COLORS.thunderGrey, fontWeight: 400, fontSize: '13px', ml: 1 }}
              >
                — {t(`${P}.${browser}.title`)}
              </Box>
            )}
          </Typography>
          {renderBrowserSteps()}
        </Box>
      </Box>
    </DefaultModal>
  )
}
